import { useEffect, useMemo, useRef, useState } from 'react'
import { Head, usePage } from '@inertiajs/react'
import Layout from '../components/Layout'
import PhotoCard from '../components/PhotoCard'
import Lightbox from '../components/Lightbox'
import UploadDialog from '../components/UploadDialog'
import useLiveRefresh from '../use_live_refresh'

function Stat({ emoji, value, label }) {
  return (
    <div className="card-cute flex items-center gap-2 px-2.5 py-2 sm:gap-3 sm:px-4 sm:py-3">
      <span className="text-lg sm:text-2xl" aria-hidden="true">
        {emoji}
      </span>
      <div>
        <p className="font-display text-base leading-none font-extrabold text-ink sm:text-xl">{value}</p>
        <p className="text-[10px] font-bold tracking-wide text-ink-soft uppercase sm:text-xs">{label}</p>
      </div>
    </div>
  )
}

function Chip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 cursor-pointer rounded-full border-2 px-3 py-1.5 font-display text-xs font-bold whitespace-nowrap transition sm:px-4 sm:text-sm ${
        active
          ? 'border-blush-400 bg-blush-400 text-white shadow'
          : 'border-white bg-white/70 text-ink-soft hover:bg-white'
      }`}
    >
      {children}
    </button>
  )
}

// How many photos to reveal per scroll batch.
const PAGE_SIZE = 12

// Module-level so the array identity is stable across renders.
const REFRESHED_PROPS = [ 'photos', 'stats' ]

// Roughly what fits above the fold on a phone, where cards are a full column wide.
const EAGER_PHOTOS = 2

export default function Gallery({ photos, stats }) {
  const { baby, currentUser } = usePage().props
  // Only admins add to the album; everyone else views and hearts it.
  const canAdd = Boolean(currentUser?.admin)
  const [filter, setFilter] = useState('all')
  const [lightboxIndex, setLightboxIndex] = useState(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [shownCount, setShownCount] = useState(PAGE_SIZE)
  const sentinel = useRef(null)

  // Pick up photos added from another device, or while the app sat in the background.
  useLiveRefresh(REFRESHED_PROPS)

  // The "Add a photo" home-screen shortcut lands on /?add=1.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (!params.has('add')) return

    if (canAdd) setUploadOpen(true)
    window.history.replaceState({}, '', window.location.pathname)
  }, [canAdd])

  const months = useMemo(() => {
    const seen = new Map()
    photos.forEach((photo) => seen.set(photo.monthKey, photo.monthLabel))
    return [...seen.entries()].map(([key, label]) => ({ key, label }))
  }, [photos])

  const visible = useMemo(() => {
    if (filter === 'all') return photos
    if (filter === 'favorites') return photos.filter((photo) => photo.favorite)
    return photos.filter((photo) => photo.monthKey === filter)
  }, [photos, filter])

  // Filters are desktop-only, so a phone always scrolls the full album. Reveal it a
  // batch at a time rather than mounting every card up front.
  const shown = useMemo(() => visible.slice(0, shownCount), [visible, shownCount])
  const hasMore = shownCount < visible.length

  useEffect(() => setShownCount(PAGE_SIZE), [filter])

  useEffect(() => {
    const node = sentinel.current
    if (!hasMore || !node) return

    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setShownCount((count) => count + PAGE_SIZE),
      { rootMargin: '600px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
    // Re-observing after each batch matters: IntersectionObserver only reports
    // *changes*, so a sentinel that stays in view would never fire a second time
    // and the album would stall short of the end.
  }, [hasMore, shownCount])

  const groups = useMemo(() => {
    const buckets = []
    shown.forEach((photo) => {
      const last = buckets[buckets.length - 1]
      if (last && last.key === photo.monthKey) {
        last.photos.push(photo)
      } else {
        buckets.push({ key: photo.monthKey, label: photo.monthLabel, photos: [photo] })
      }
    })
    return buckets
  }, [shown])

  return (
    <Layout>
      <Head title="The album" />

      <section className="mb-4 sm:mb-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl leading-tight font-extrabold text-ink sm:text-5xl">
              {baby?.name || 'Our baby'}&rsquo;s album
            </h1>
            <p className="mt-1 max-w-xl text-sm text-ink-soft sm:mt-2 sm:text-lg">
              {baby?.tagline || 'Every wiggle, yawn and milestone — kept just for friends and family.'}
            </p>
          </div>

          {canAdd && (
            <button
              type="button"
              onClick={() => setUploadOpen(true)}
              className="btn-cute hidden bg-blush-400 py-3 text-lg text-white shadow-lg hover:bg-blush-500 sm:inline-flex"
            >
              📸 Add a photo
            </button>
          )}
        </div>

        {/* Wraps rather than scrolls: the tile count varies with videos and baby age,
            and a scroll row leaves the last tile sliced at the screen edge. */}
        <div className="mt-3 flex flex-wrap gap-2 sm:mt-6 sm:gap-3">
          <Stat emoji="🖼️" value={stats.photos} label={stats.photos === 1 ? 'Photo' : 'Photos'} />
          {stats.videos > 0 && (
            <Stat emoji="🎬" value={stats.videos} label={stats.videos === 1 ? 'Video' : 'Videos'} />
          )}
          <Stat emoji="⭐️" value={stats.favorites} label="Favourites" />
          <Stat emoji="👨‍👩‍👧" value={stats.contributors} label="Sharing" />
          {baby?.age && <Stat emoji="🎂" value={baby.age} label="Right now" />}
        </div>
      </section>

      {/* Desktop only: on a phone the row costs more vertical space than it is
          worth, and the album scrolls in full instead. */}
      {photos.length > 0 && (
        <div className="hidden gap-2 pb-2 sm:mb-6 sm:flex sm:flex-wrap">
          <Chip active={filter === 'all'} onClick={() => setFilter('all')}>
            Everything
          </Chip>
          <Chip active={filter === 'favorites'} onClick={() => setFilter('favorites')}>
            ⭐️ Favourites
          </Chip>
          {months.map((month) => (
            <Chip key={month.key} active={filter === month.key} onClick={() => setFilter(month.key)}>
              {month.label}
            </Chip>
          ))}
        </div>
      )}

      {visible.length === 0 ? (
        <div className="card-cute animate-pop-in px-6 py-16 text-center">
          <span className="animate-float-slow inline-block text-6xl" aria-hidden="true">
            🧸
          </span>
          <h2 className="mt-4 font-display text-2xl font-extrabold text-ink">
            {photos.length === 0 ? 'The album is empty' : 'Nothing here yet'}
          </h2>
          <p className="mt-2 text-ink-soft">
            {photos.length > 0
              ? 'Try a different filter to find more photos.'
              : canAdd
                ? 'Add the very first photo or video and give the grandparents something to coo over.'
                : 'Nothing here yet — mum or dad will add the first photos soon.'}
          </p>
          {photos.length === 0 && canAdd && (
            <button
              type="button"
              onClick={() => setUploadOpen(true)}
              className="btn-cute mt-6 bg-blush-400 text-white shadow-lg hover:bg-blush-500"
            >
              📸 Add the first photo
            </button>
          )}
        </div>
      ) : (
        groups.map((group) => (
          <section key={group.key} className="mb-6 sm:mb-10">
            <h2 className="mb-3 flex items-center gap-3 font-display text-lg font-extrabold text-ink sm:mb-4 sm:text-2xl">
              <span className="rounded-full bg-white/80 px-3 py-1 shadow-sm sm:px-4">{group.label}</span>
              <span className="h-1 flex-1 rounded-full bg-white/60" aria-hidden="true" />
            </h2>

            {/* One stacked image per row on phones; masonry columns from sm up. */}
            <div className="grid grid-cols-1 gap-4 sm:block sm:columns-2 sm:gap-5 lg:columns-3">
              {group.photos.map((photo) => (
                <PhotoCard
                  key={photo.id}
                  photo={photo}
                  priority={shown.indexOf(photo) < EAGER_PHOTOS}
                  onOpen={() => setLightboxIndex(visible.indexOf(photo))}
                />
              ))}
            </div>
          </section>
        ))
      )}

      {hasMore && (
        <div ref={sentinel} className="flex justify-center py-6" aria-hidden="true">
          <span className="animate-float-slow text-3xl">🍼</span>
        </div>
      )}

      {lightboxIndex !== null && (
        <Lightbox
          photos={visible}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={(next) => setLightboxIndex(Math.min(Math.max(next, 0), visible.length - 1))}
        />
      )}

      {canAdd && (
      <button
        type="button"
        onClick={() => setUploadOpen(true)}
        aria-label="Add a photo"
        className="fixed right-4 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-40 flex h-16 w-16 cursor-pointer items-center justify-center rounded-full border-4 border-white bg-blush-400 text-3xl shadow-xl transition active:scale-95 sm:hidden"
      >
        <span aria-hidden="true">📸</span>
      </button>
      )}

      {canAdd && <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />}
    </Layout>
  )
}
